import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { apiService } from "../services/apiService";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";
import logger from "../utils/logger";
import HistoryFilters from "./history/HistoryFilters";
import HistoryTable from "./history/HistoryTable";
import HistoryDeleteConfirm from "./history/HistoryDeleteConfirm";
import HistoryEditModal from "./history/HistoryEditModal";

const DETECTION_TYPE_BY_TABLE = {
  mms_pill_counting: "mms_bottle",
  ttd_capsule_counting: "ttd_capsule",
};

const doesRecordBelongToOtherUser = (record, currentUser) => {
  if (!currentUser || !record) return false;
  const currentUserId = currentUser.id !== undefined && currentUser.id !== null ? String(currentUser.id).trim() : null;
  const currentEmail = currentUser.email ? String(currentUser.email).trim().toLowerCase() : null;
  const currentName = currentUser.name ? String(currentUser.name).trim().toLowerCase() : null;

  const recUserId = record.id_user_chp !== undefined && record.id_user_chp !== null ? String(record.id_user_chp).trim() : null;
  const recChpName = record.nama_chp ? String(record.nama_chp).trim().toLowerCase() : null;

  if (recUserId) {
    const matchesId = currentUserId && recUserId === currentUserId;
    const matchesEmail = currentEmail && recUserId.toLowerCase() === currentEmail;
    if (!matchesId && !matchesEmail) {
      return true;
    }
  } else if (recChpName) {
    const matchesName = currentName && recChpName === currentName;
    const matchesEmail = currentEmail && recChpName === currentEmail;
    if (!matchesName && !matchesEmail) {
      return true;
    }
  }

  return false;
};

const HistoryScreen = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterDate, setFilterDate] = useState("");
  const [filterPillType, setFilterPillType] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editForm, setEditForm] = useState({
    pill_count: "",
    notes: "",
    source: "",
    chp_name: "",
    detection_type: "",
  });
  const navigate = useNavigate();

  useEffect(() => {
    loadRecords();
  }, [user]);

  const loadRecords = async () => {
    setLoading(true);
    try {
      logger.debug("Loading counting history...");

      // Load counting records dari 3 table BigQuery (mms_pill_counting, calcium_tablet_counting, ttd_capsule_counting)
      const countingRecords = await apiService.getCountingHistory(1000);

      // Defense-in-depth: Filter out any records belonging to another user
      const userCountingRecords = (countingRecords || []).filter(
        (record) => !doesRecordBelongToOtherUser(record, user)
      );

      logger.debug(`Received ${userCountingRecords?.length || 0} records from API`);
      logger.debug("Sample record:", userCountingRecords?.[0]);
      if (userCountingRecords && userCountingRecords.length > 0) {
        logger.debug("Sample record fields:", {
          nama_chp: userCountingRecords[0].nama_chp,
          nama_ibu: userCountingRecords[0].nama_ibu,
          barcode_id: userCountingRecords[0].barcode_id,
          jenis_counting: userCountingRecords[0].jenis_counting,
        });
      }

      if (!userCountingRecords || userCountingRecords.length === 0) {
        logger.debug("No records found, setting empty array");
        setRecords([]);
        return;
      }

      // Transform data untuk kompatibilitas dengan UI yang ada
      const transformedRecords = userCountingRecords.map((record, index) => {
        // Generate unique ID
        const recordId = record.table
          ? `${record.table}_${record.barcode_id || record.id_barcode}_${
              record.tanggal_submit || index
            }`
          : `record_${index}_${Date.now()}`;

        // Debug: Log raw record untuk melihat struktur data
        if (index === 0) {
          logger.debug("Raw record object:", record);
          logger.debug("Record keys:", Object.keys(record));
          logger.debug(
            "nama_chp value:",
            record.nama_chp,
            "type:",
            typeof record.nama_chp
          );
          logger.debug(
            "nama_ibu value:",
            record.nama_ibu,
            "type:",
            typeof record.nama_ibu
          );
        }

        return {
          id: recordId,
          barcode_id: record.barcode_id || record.id_barcode || "-",
          mother_name:
            record.nama_ibu && String(record.nama_ibu).trim() !== ""
              ? String(record.nama_ibu)
              : "N/A", // Menggunakan nama_ibu dari database
          chp_name:
            record.nama_chp && String(record.nama_chp).trim() !== ""
              ? String(record.nama_chp)
              : "N/A", // Menggunakan nama_chp dari database
          pill_count: record.jumlah_pills || 0,
          source: "manual", // Default untuk counting records
          confidence: null,
          timestamp: record.tanggal_submit || new Date().toISOString(),
          notes: null,
          detection_type: DETECTION_TYPE_BY_TABLE[record.table] || "unknown",
          supplement_id: null,
          mother_id: null,
          // Tambahkan field untuk identifikasi
          table_source: record.table || "unknown",
          jenis_counting: record.jenis_counting || "Unknown",
          id_ibu: record.id_ibu || null,
          id_user_chp: record.id_user_chp || null,
          capture_id: record.capture_id || null,
          yolo_image: record.yolo_image || null,
          jumlah_pills_koreksi: record.jumlah_pills_koreksi ?? null,
          model_version: record.model_version || null,
          image_url:
            record.image_url ||
            (record.capture_id
              ? apiService.getCaptureImageUrl(
                  record.capture_id,
                  DETECTION_TYPE_BY_TABLE[record.table] || "mms_bottle"
                )
              : null),
        };
      });

      logger.debug(`Transformed ${transformedRecords.length} records`);
      if (transformedRecords.length > 0) {
        const sample = transformedRecords[0];
        logger.debug("Sample transformed record:", {
          chp_name: sample.chp_name,
          mother_name: sample.mother_name,
          barcode_id: sample.barcode_id,
          raw_nama_chp: countingRecords[0]?.nama_chp,
          raw_nama_ibu: countingRecords[0]?.nama_ibu,
        });
        logger.debug("Full sample record object:", sample);
      }
      setRecords(transformedRecords);
    } catch (error) {
      logger.apiError("getCountingHistory", error);

      const errorMessage =
        error.response?.data?.detail ||
        error.message ||
        "Failed to load records";
      toast.error(errorMessage);

      // Set empty array on error to prevent UI issues
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const getCountType = (record) => {
    // Jika ada jenis_counting dari counting table, gunakan itu
    if (record.jenis_counting) {
      return record.jenis_counting;
    }

    // Determine count type based on detection_type or supplement_type
    if (record.detection_type) {
      switch (record.detection_type) {
        case "mms_blister":
          return "MMS Blister";
        case "ttd_capsule":
        case "ttd capsule":
          return "TTD Capsule";
        case "mms_bottle":
        case "mms pills":
          return "MMS Botol";
        default:
          return "MMS Pills";
      }
    }

    if (record.supplement_type) {
      switch (record.supplement_type) {
        case "iron":
          return "MMS Pills";
        case "folic_acid":
          return "MMS Pills";
        default:
          return "MMS Pills";
      }
    }

    return "MMS Pills"; // Default fallback
  };

  const filteredRecords = records.filter((record) => {
    const matchesSearch =
      !searchTerm ||
      record.mother_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.barcode_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.id_ibu?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.id_user_chp?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.chp_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.jenis_counting?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesDate =
      !filterDate ||
      (record.timestamp &&
        new Date(record.timestamp).toISOString().startsWith(filterDate));

    const matchesPillType = !filterPillType || getCountType(record) === filterPillType;

    return matchesSearch && matchesDate && matchesPillType;
  });


  const handleViewRecord = (record) => {
    // Navigate to results screen with record data
    navigate("/results", {
      state: {
        record,
        image: record.image_url,
        capture_id: record.capture_id,
        motherInfo: {
          mother_id: record.mother_id,
          mother_name: record.mother_name,
          supplement_id: record.supplement_id,
          barcode_id: record.barcode_id,
        },
        aiResult:
          record.source === "ai"
            ? {
                count: record.pill_count,
                confidence: record.confidence,
                capture_id: record.capture_id,
                image_url: record.image_url,
              }
            : null,
      },
    });
  };

  const handleEditRecord = (record) => {
    setEditingRecord(record);
    setEditForm({
      pill_count: record.pill_count.toString(),
      notes: record.notes || "",
      source: record.source,
      chp_name: record.chp_name || "",
      detection_type: record.detection_type || "",
    });
    setShowEditModal(true);
  };

  const handleEditFormChange = (field, value) => {
    setEditForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveEdit = async () => {
    if (!editingRecord) return;

    try {
      // Check if this is a counting record (from BigQuery counting tables)
      if (editingRecord.table_source) {
        // Update counting record di BigQuery
        const tableName = editingRecord.table_source;
        const barcodeId = editingRecord.barcode_id;
        const tanggalSubmit = editingRecord.timestamp; // ISO format string

        await apiService.updateCountingRecord(
          tableName,
          barcodeId,
          tanggalSubmit,
          parseInt(editForm.pill_count)
        );

        // Reload records dari server
        await loadRecords();

        toast.success("Record updated successfully");
      } else {
        // Legacy: Update local state only (for old records table)
        const updatedRecords = records.map((record) =>
          record.id === editingRecord.id
            ? {
                ...record,
                pill_count: parseInt(editForm.pill_count),
                notes: editForm.notes,
                source: editForm.source,
                chp_name: editForm.chp_name,
                detection_type: editForm.detection_type,
              }
            : record
        );

        setRecords(updatedRecords);
        toast.success("Record updated successfully");
      }

      setShowEditModal(false);
      setEditingRecord(null);
    } catch (error) {
      logger.apiError("updateCountingRecord", error);
      const errorMessage =
        error.response?.data?.detail ||
        error.message ||
        "Failed to update record";
      toast.error(errorMessage);
    }
  };

  const handleCancelEdit = () => {
    setShowEditModal(false);
    setEditingRecord(null);
    setEditForm({
      pill_count: "",
      notes: "",
      source: "",
    });
  };

  const handleDeleteRecord = (record) => {
    setRecordToDelete(record);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!recordToDelete) return;

    setIsDeleting(true);
    try {
      // Check if this is a counting record (from BigQuery counting tables)
      if (recordToDelete.table_source) {
        // Delete counting record dari BigQuery
        const tableName = recordToDelete.table_source;
        const barcodeId = recordToDelete.barcode_id;
        const tanggalSubmit = recordToDelete.timestamp; // ISO format string

        await apiService.deleteCountingRecord(
          tableName,
          barcodeId,
          tanggalSubmit
        );

        // Reload records dari server
        await loadRecords();

        toast.success("Record deleted successfully");
      } else {
        // Legacy: Delete from old records table
        await apiService.deleteRecord(recordToDelete.id);

        // Update local state
        setRecords(records.filter((r) => r.id !== recordToDelete.id));
        toast.success("Record deleted successfully");
      }
    } catch (error) {
      logger.apiError("deleteCountingRecord", error);
      const errorMessage =
        error.response?.data?.detail ||
        error.message ||
        "Failed to delete record";
      toast.error(errorMessage);
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
      setRecordToDelete(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center">
              <button
                onClick={() => navigate("/")}
                className="mr-4 p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                Count History
              </h1>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Filters */}
        <HistoryFilters
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          filterDate={filterDate}
          setFilterDate={setFilterDate}
          filterPillType={filterPillType}
          setFilterPillType={setFilterPillType}
        />

        <HistoryTable
          records={filteredRecords}
          loading={loading}
          isAdmin={isAdmin}
          handleViewRecord={handleViewRecord}
          handleEditRecord={handleEditRecord}
          handleDeleteRecord={handleDeleteRecord}
          getCountType={getCountType}
          totalRecords={records.length}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <HistoryDeleteConfirm
        showDeleteConfirm={showDeleteConfirm}
        recordToDelete={recordToDelete}
        isDeleting={isDeleting}
        onCancel={() => {
          if (!isDeleting) {
            setShowDeleteConfirm(false);
            setRecordToDelete(null);
          }
        }}
        onConfirm={confirmDelete}
      />

      {/* Edit Modal */}
      <HistoryEditModal
        showEditModal={showEditModal}
        editingRecord={editingRecord}
        editForm={editForm}
        handleEditFormChange={handleEditFormChange}
        handleSaveEdit={handleSaveEdit}
        handleCancelEdit={handleCancelEdit}
      />
    </div>
  );
};

export default HistoryScreen;
